import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaCheck, FaTimes, FaTrash } from 'react-icons/fa';

const getEventType = (event) => event?.eventType || event?.event_type || 'deposit';

const ReferralTicketManager = () => {
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const referralEvents = events.filter((e) => getEventType(e) === 'referral');

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const [ticketsRes, eventsRes] = await Promise.all([
        axios.get('/api/tickets/admin/referrals', {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: searchTerm || undefined,
            eventId: selectedEvent || undefined
          }
        }),
        axios.get('/api/tickets/events/admin', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTickets(ticketsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching referral tickets:', error);
    }
  }, [searchTerm, selectedEvent]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (ticketId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `/api/tickets/${ticketId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Durum güncellenirken bir hata oluştu');
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Bu referans talebini silmek istediğinize emin misiniz?')) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      alert('Talep silinirken bir hata oluştu');
    }
  };

  const pendingCount = tickets.filter((t) => (t.status || '') === 'pending').length;
  const approvedCount = tickets.filter((t) => (t.status || '') === 'approved').length;
  const rejectedCount = tickets.filter((t) => (t.status || '') === 'rejected').length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gold">Referans Davet Talepleri</h2>
        <p className="text-white/60 text-sm mt-1">
          Sadece referans bilet etkinliklerinden gelen arkadaş kayıt başvuruları
        </p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-card rounded-xl p-4 border border-gold/30">
          <div className="text-white/60 text-xs mb-1">TOPLAM</div>
          <div className="text-white text-2xl font-bold">{tickets.length}</div>
        </div>
        <div className="bg-gradient-card rounded-xl p-4 border border-yellow-500/30">
          <div className="text-white/60 text-xs mb-1">BEKLEYEN</div>
          <div className="text-yellow-400 text-2xl font-bold">{pendingCount}</div>
        </div>
        <div className="bg-gradient-card rounded-xl p-4 border border-green-500/30">
          <div className="text-white/60 text-xs mb-1">ONAYLANAN</div>
          <div className="text-green-400 text-2xl font-bold">{approvedCount}</div>
        </div>
        <div className="bg-gradient-card rounded-xl p-4 border border-red-500/30">
          <div className="text-white/60 text-xs mb-1">REDDEDİLEN</div>
          <div className="text-red-400 text-2xl font-bold">{rejectedCount}</div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder="Kullanıcı veya arkadaş adı ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
        />
        <select
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
          className="px-4 py-2 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
        >
          <option value="">Tüm referans etkinlikleri</option>
          {referralEvents.map((event) => (
            <option key={event.id} value={event.id}>
              {event.title}
            </option>
          ))}
        </select>
      </div>

      {referralEvents.length === 0 && (
        <div className="bg-amber-900/20 border border-amber-500/40 rounded-lg p-4 mb-6 text-amber-200 text-sm">
          Henüz referans tipinde bilet etkinliği yok. Bilet Etkinlikleri sayfasından &quot;Referans / Arkadaş Daveti&quot; tipinde etkinlik oluşturun.
        </div>
      )}

      <div className="space-y-4">
        {tickets.length === 0 ? (
          <p className="text-white/60 text-center py-8">Referans talebi bulunamadı.</p>
        ) : (
          tickets.map((ticket) => {
            const status = ticket.status;
            const eventTitle = ticket.ticketEvent?.title || ticket.event_title || '—';
            const sponsorName = ticket.sponsorName || ticket.sponsor_name || '—';
            const friendUsername = ticket.siteUsername || ticket.site_username || '—';
            const inviter = ticket.user?.username || ticket.username || '—';
            const ticketsPerFriend =
              ticket.ticketEvent?.referralTicketsPerFriend ||
              ticket.referral_tickets_per_friend ||
              ticket.ticketCount ||
              ticket.ticket_count ||
              1;

            return (
              <div
                key={ticket.id}
                className="bg-gradient-card rounded-xl p-5 border border-gold/30"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gold">{eventTitle}</h3>
                    <p className="text-white/60 text-sm">
                      {new Date(ticket.createdAt || ticket.created_at).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-3 py-1 rounded-full font-semibold ${
                      status === 'approved'
                        ? 'bg-green-500 text-white'
                        : status === 'rejected'
                          ? 'bg-red-500 text-white'
                          : 'bg-yellow-500 text-navy-900'
                    }`}
                  >
                    {status === 'approved'
                      ? 'Onaylandı'
                      : status === 'rejected'
                        ? 'Reddedildi'
                        : 'Bekliyor'}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 text-sm">
                  <div>
                    <div className="text-white/60 mb-1">Davet eden üye</div>
                    <div className="text-white font-semibold">{inviter}</div>
                  </div>
                  <div>
                    <div className="text-white/60 mb-1">Kayıt olunan site</div>
                    <div className="text-white font-semibold">{sponsorName}</div>
                  </div>
                  <div>
                    <div className="text-white/60 mb-1">Arkadaş kullanıcı adı</div>
                    <div className="text-white font-semibold">{friendUsername}</div>
                  </div>
                  <div>
                    <div className="text-white/60 mb-1">Kazanılacak bilet</div>
                    <div className="text-gold font-bold">{ticketsPerFriend} adet</div>
                  </div>
                </div>

                {status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => handleStatusChange(ticket.id, 'approved')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    >
                      <FaCheck /> Onayla
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStatusChange(ticket.id, 'rejected')}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    >
                      <FaTimes /> Reddet
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(ticket.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-navy-700 hover:bg-navy-600 text-white/80 rounded-lg transition-colors ml-auto"
                    >
                      <FaTrash /> Sil
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

    </div>
  );
};

export default ReferralTicketManager;

